// Setup › Job forms on the SettingsPage pattern (BL-189): the forms techs fill on the iPad (forms.list),
// where each is asked for, whether it's required to complete the job, and its questions. As Forms.tsx's
// FormsTab draws them in Blueprint, with nothing changed: Add a form and each form's Edit open a
// FormSheet, each question a Section in it; a change to the questions is a new version, and a form
// already filled keeps the version it was filled on (forms.update). Owner and manager (org.edit). While
// the company mirrors its import source, the forms it brought are its own to change (BL-088). The items
// that call for a form are still picked with the estimate editor's PricebookPicker, until ItemPicker is
// wired to the price book (BL-191). Riverstone keeps FormsTab.
import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  KeyValue,
  Link,
  SelectField,
  TextField,
  type SelectOption,
} from '@flowology/ui-web/fl';
import { FormSheet, Section, SettingRow, SettingsPage, SettingsSection } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { usePaged } from '../blueprint/paged.ts';
import { refusal } from '../history/customer-edit-model.ts';
import { PricebookPicker } from '../job/PricebookPicker.tsx';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { MIRROR_LINE, useMirror } from '../shared/mirror.ts';
import { shownText } from '../shared/pages.ts';
import { useSaveKey } from '../shared/save-key.ts';
import {
  COMPLETED_WORK_KEY,
  CUSTOMER_CHOICES,
  emptyForm,
  emptyQuestion,
  FIELD_KINDS,
  formEditable,
  formForm,
  formInput,
  formScope,
  makesNewVersion,
  MAX_FORM_ITEMS,
  moveQuestion,
  offeredKinds,
  offJobInput,
  offJobWords,
  requiredChip,
  WHEN_CHOICES,
  type FieldKind,
  type FormForm,
  type JobForm,
  type OffJobPage,
  type OffJobSubmission,
  type QuestionForm,
} from './forms-model.ts';
import { BP_LEDE, LEDE } from './ledes.ts';
import { settingsPage } from './parts.ts';
import { settingsRights } from './settings-model.ts';
import { setupRights } from './setup-model.ts';
import { SetupRuleSections } from './SetupRules.tsx';

interface Named {
  id: string;
  name: string;
  businessUnitId?: string | null;
}
interface LicenceKind {
  id: string;
  name: string;
  active: boolean;
}

const options = (pairs: readonly (readonly [string, string])[]): SelectOption[] =>
  pairs.map(([value, label]) => ({ value, label }));

export function FormsPage({ nav }: { nav: NavProps }) {
  const held = nav.permissions ?? [];
  const rights = settingsRights(held);
  const crumb = usePageCrumb(nav);
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: settingsPage('forms').label ?? 'Job forms',
            lede: BP_LEDE.forms ?? LEDE.forms,
          }}
        >
          <SetupRuleSections page="forms" top rights={rights} />
          {setupRights(held).org && <Forms />}
          <SetupRuleSections page="forms" rights={rights} />
        </SettingsPage>
      </main>
    </div>
  );
}

function Forms() {
  const [forms, setForms] = useState<JobForm[] | null>(null);
  const [units, setUnits] = useState<Named[]>([]);
  const [types, setTypes] = useState<Named[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<JobForm | 'new' | null>(null);
  const [offJob, setOffJob] = useState<JobForm | null>(null);
  const [said, setSaid] = useState<string | null>(null);
  // while mirroring, the import source's forms are its own to change; Flowology's stay editable (BL-088)
  const mirroring = useMirror();
  const load = useCallback(async () => {
    try {
      setForms((await client.call<{ forms: JobForm[] }>('forms.list', {})).forms);
      setError(null);
    } catch (e) {
      setError(refusal(e));
    }
  }, []);
  useEffect(() => {
    void load();
    client
      .call<{ businessUnits: Named[] }>('businessUnits.list', {})
      .then((r) => setUnits(r.businessUnits))
      .catch(() => undefined);
    client
      .call<{ jobTypes: Named[] }>('jobTypes.list', {})
      .then((r) => setTypes(r.jobTypes))
      .catch(() => undefined);
  }, [load]);
  const done = (text: string) => {
    setSaid(text);
    void load();
  };
  return (
    <>
      <SettingsSection
        title="Your forms"
        line="What techs fill in on the iPad: on every job, or only on one department’s or job type’s, for one kind of customer, or when an item is on the invoice. A required form must be done before the office completes the job; one required on arrival comes before the work starts."
        action={
          <Button variant="primary" onPress={() => setOpen('new')}>
            Add a form
          </Button>
        }
        {...(said ? { note: <span role="status">{said}</span> } : {})}
        status={forms ? (forms.length ? 'ready' : 'empty') : error ? 'failed' : 'loading'}
        emptyText="No forms yet. Add the first one."
        {...(error ? { failedText: error } : {})}
        onRetry={() => void load()}
      >
        {forms?.map((f) => {
          const n = f.fields.length;
          const tag = [requiredChip(f), `Version ${f.version ?? 1}`, !f.active && 'Retired']
            .filter(Boolean)
            .join(' · ');
          const off = offJobWords(f);
          const editable = formEditable(f, mirroring);
          return (
            <SettingRow
              key={f.id}
              name={f.name}
              tag={tag}
              help={
                <>
                  {formScope(f, units, types)}
                  <br />
                  {n} question{n === 1 ? '' : 's'}
                  {f.submissions ? ` · filled in ${f.submissions} time${f.submissions === 1 ? '' : 's'}` : ''}
                  {off && (
                    <>
                      <br />
                      <Link onPress={() => setOffJob(f)}>{off}</Link>
                    </>
                  )}
                  {!editable && (
                    <>
                      <br />
                      {MIRROR_LINE}
                    </>
                  )}
                </>
              }
              control={
                editable ? (
                  <Link label={`Edit ${f.name}`} onPress={() => setOpen(f)}>
                    Edit
                  </Link>
                ) : null
              }
            />
          );
        })}
      </SettingsSection>
      {/* over the page: a section's body is its empty or failed line until it has rows */}
      {offJob && <OffJobSheet form={offJob} onClose={() => setOffJob(null)} />}
      {open && (open === 'new' || formEditable(open, mirroring)) && (
        <JobFormSheet
          key={open === 'new' ? 'new' : open.id}
          form={open === 'new' ? null : open}
          units={units}
          types={types}
          onDone={done}
          onClose={() => setOpen(null)}
        />
      )}
    </>
  );
}

/** A form's submissions the import source filed on no job (#81): the company's own, read only. */
function OffJobSheet({ form, onClose }: { form: JobForm; onClose: () => void }) {
  // a page at a time, "Load more" reading on (list caps M10): years of a company's own forms
  const list = usePaged<OffJobSubmission, OffJobPage>(
    'forms.offJob',
    offJobInput(form.id),
    (p) => p.submissions,
  );
  const rows = list.rows;
  const total = list.first?.total ?? rows?.length ?? 0;
  return (
    <FormSheet
      isOpen
      isReadOnly
      onClose={onClose}
      title={`${form.name}: on no job`}
      isDirty={false}
      onSubmit={() => undefined}
    >
      {!rows ? (
        <p role={list.error ? 'alert' : 'status'}>{list.error ?? 'Loading the forms…'}</p>
      ) : (
        <>
          {rows.map((r) => (
            <Section
              key={r.id}
              title={`${new Date(r.submittedAt).toLocaleDateString()}${r.by ? ` · ${r.by}` : ''}`}
            >
              <KeyValue
                items={r.answers.map((a) => ({ label: a.question, value: a.answer }))}
                isStackedOnPhone
              />
            </Section>
          ))}
          {(list.more || list.busy) && (
            <p>
              {shownText(rows.length, total)}{' '}
              <Link isDisabled={list.busy} onPress={() => list.more?.()}>
                {list.busy ? 'Loading…' : 'Load more'}
              </Link>
            </p>
          )}
        </>
      )}
    </FormSheet>
  );
}

/** The licences a licensed signer can hold (licenceKinds.list), the retired ones too. */
function useLicenceKinds(): LicenceKind[] | null {
  const [kinds, setKinds] = useState<LicenceKind[] | null>(null);
  useEffect(() => {
    client
      .call<{ kinds: LicenceKind[] }>('licenceKinds.list', { includeInactive: true })
      .then((r) => setKinds(r.kinds))
      // the picker stays empty; a licensed form can't be saved without one
      .catch(() => setKinds([]));
  }, []);
  return kinds;
}

function JobFormSheet({
  form,
  units,
  types,
  onDone,
  onClose,
}: {
  form: JobForm | null;
  units: Named[];
  types: Named[];
  onDone: (said: string) => void;
  onClose: () => void;
}) {
  const [first] = useState<FormForm>(() => (form ? formForm(form) : emptyForm()));
  const [f, setF] = useState<FormForm>(first);
  const save = useSaveKey(JSON.stringify(f));
  const life = useSaveKey(`life:${form?.id ?? ''}`);
  const [lifeBusy, setLifeBusy] = useState(false);
  const [lifeError, setLifeError] = useState<string | null>(null);
  const completedWork = form?.key === COMPLETED_WORK_KEY;
  const kinds = offeredKinds();
  const licenceKinds = useLicenceKinds();
  const set = (patch: Partial<FormForm>) => setF({ ...f, ...patch });
  const setQ = (i: number, patch: Partial<QuestionForm>) =>
    set({ questions: f.questions.map((q, j) => (j === i ? { ...q, ...patch } : q)) });
  const typesHere = types.filter(
    (t) => !f.businessUnitId || !t.businessUnitId || t.businessUnitId === f.businessUnitId,
  );
  const newVersion = makesNewVersion(form, f);
  const submit = async () => {
    const built = formInput(form, f);
    if ('error' in built) throw new Error(built.error);
    let r: JobForm;
    try {
      r = await client.call<JobForm>(form ? 'forms.update' : 'forms.define', built.input, {
        idempotencyKey: save.key,
      });
    } catch (e) {
      throw new Error(refusal(e), { cause: e });
    }
    save.renew();
    onDone(
      form
        ? r.version !== form.version
          ? `${r.name} is saved as version ${r.version}. Forms already filled keep version ${form.version}.`
          : `${r.name} is saved.`
        : `${r.name} is added. Techs see it on the iPad on the jobs it’s for.`,
    );
  };
  const flip = async () => {
    if (!form) return;
    setLifeBusy(true);
    setLifeError(null);
    try {
      const r = await client.call<JobForm>(
        form.active ? 'forms.retire' : 'forms.restore',
        { id: form.id, rowVersion: form.rowVersion },
        { idempotencyKey: life.key },
      );
      life.renew();
      onDone(
        r.active
          ? `${r.name} is in use again.`
          : `${r.name} is retired. Forms already filled stay as they are.`,
      );
      onClose();
    } catch (e) {
      setLifeError(refusal(e));
    } finally {
      setLifeBusy(false);
    }
  };
  return (
    <FormSheet
      isOpen
      onClose={onClose}
      title={form ? form.name : 'Add a job form'}
      submitLabel={form ? 'Save' : 'Add form'}
      isDirty={JSON.stringify(f) !== JSON.stringify(first)}
      {...(form
        ? {
            note: newVersion
              ? `Saving makes version ${(form.version ?? 1) + 1}. Forms already filled keep the version they were filled on.`
              : `Version ${form.version ?? 1}. Changing the questions makes a new version; forms already filled keep theirs.`,
          }
        : {})}
      onSubmit={submit}
    >
      <TextField label="Name" value={f.name} autoFocus onChange={(name) => set({ name })} />
      <Checkbox
        isSelected={f.requiredOnCompletion}
        isDisabled={completedWork}
        onChange={(requiredOnCompletion) => set({ requiredOnCompletion })}
      >
        Required to complete the job{completedWork ? ' (always, for Completed work)' : ''}
      </Checkbox>
      {f.requiredOnCompletion && !completedWork && (
        <SelectField
          label="When the tech fills it in"
          value={f.requiredAt}
          options={options(WHEN_CHOICES)}
          onChange={(v) => set({ requiredAt: v as FormForm['requiredAt'] })}
        />
      )}
      {!completedWork && (
        <>
          <Checkbox
            isSelected={f.licensedSigner}
            onChange={(on) => set({ licensedSigner: on, licenceKindId: on ? f.licenceKindId : '' })}
          >
            Only someone licensed fills it in (the office can’t for them)
          </Checkbox>
          {f.licensedSigner && (
            <SelectField
              label="The licence they hold"
              value={f.licenceKindId}
              {...(licenceKinds && !licenceKinds.length
                ? {
                    description: 'Add the licences you track first, in Settings › Departments and job types.',
                  }
                : {})}
              options={[
                { value: '', label: 'Choose…' },
                ...(licenceKinds ?? [])
                  .filter((k) => k.active || k.id === f.licenceKindId)
                  .map((k) => ({ value: k.id, label: k.name })),
              ]}
              onChange={(licenceKindId) => set({ licenceKindId })}
            />
          )}
        </>
      )}
      <SelectField
        label="Department"
        value={f.businessUnitId}
        options={[
          { value: '', label: 'Every department' },
          ...units.map((u) => ({ value: u.id, label: u.name })),
        ]}
        onChange={(businessUnitId) => set({ businessUnitId, jobTypeId: '' })}
      />
      <SelectField
        label="Job type"
        value={f.jobTypeId}
        options={[
          { value: '', label: 'Every job type' },
          ...typesHere.map((t) => ({ value: t.id, label: t.name })),
        ]}
        onChange={(jobTypeId) => set({ jobTypeId })}
      />
      {!completedWork && (
        <SelectField
          label="Customers"
          value={f.customerKind}
          options={options(CUSTOMER_CHOICES)}
          onChange={(v) => set({ customerKind: v as FormForm['customerKind'] })}
        />
      )}
      {!completedWork && (
        <Section title="Only when the invoice has">
          <p>
            {f.items.length
              ? 'Asked for once one of these is on the job’s invoice.'
              : 'No items: asked for whatever is on the invoice.'}
          </p>
          {f.items.length > 0 && (
            <KeyValue
              layout="spread"
              label="Items that call for it"
              items={f.items.map((i) => ({
                label: i.name,
                value: (
                  <Link
                    label={`Take ${i.name} off`}
                    onPress={() => set({ items: f.items.filter((x) => x.id !== i.id) })}
                  >
                    Remove
                  </Link>
                ),
              }))}
            />
          )}
          {f.items.length < MAX_FORM_ITEMS && (
            <PricebookPicker
              label="Add an item"
              onPick={(item) =>
                f.items.some((x) => x.id === item.id)
                  ? undefined
                  : set({ items: [...f.items, { id: item.id, name: item.name }] })
              }
            />
          )}
        </Section>
      )}
      {f.questions.map((q, i) => {
        const n = i + 1;
        // a kind the builder doesn't offer yet stays choosable on a question that already has it
        const here = kinds.some(([k]) => k === q.type)
          ? kinds
          : [...kinds, FIELD_KINDS.find(([k]) => k === q.type)!];
        return (
          <Section key={i} title={`Question ${n}`}>
            <TextField
              label={`Question ${n}`}
              value={q.label}
              placeholder="What the tech answers, e.g. Leak found"
              onChange={(label) => setQ(i, { label })}
            />
            <SelectField
              label="Kind of answer"
              value={q.type}
              options={options(here)}
              onChange={(v) => setQ(i, { type: v as FieldKind })}
            />
            {q.type === 'choice' && (
              <TextField
                label="Answers to pick from, one per line"
                multiline
                value={q.options}
                onChange={(o) => setQ(i, { options: o })}
              />
            )}
            <TextField
              label="Help for the tech (optional)"
              value={q.help}
              maxLength={200}
              placeholder="A line under the question, e.g. Measure at the hose bib"
              onChange={(help) => setQ(i, { help })}
            />
            <Checkbox isSelected={q.required} onChange={(required) => setQ(i, { required })}>
              Required
            </Checkbox>
            <p>
              <Link
                label={`Move question ${n} up`}
                isDisabled={i === 0}
                onPress={() => set({ questions: moveQuestion(f.questions, i, -1) })}
              >
                Up
              </Link>{' '}
              <Link
                label={`Move question ${n} down`}
                isDisabled={i === f.questions.length - 1}
                onPress={() => set({ questions: moveQuestion(f.questions, i, 1) })}
              >
                Down
              </Link>{' '}
              <Link
                label={`Remove question ${n}`}
                isDisabled={f.questions.length === 1}
                onPress={() => set({ questions: f.questions.filter((_, j) => j !== i) })}
              >
                Remove
              </Link>
            </p>
          </Section>
        );
      })}
      <Button onPress={() => set({ questions: [...f.questions, emptyQuestion()] })}>Add a question</Button>
      {form && !completedWork && (
        <p>
          <Link isDisabled={lifeBusy} onPress={() => void flip()}>
            {lifeBusy
              ? form.active
                ? 'Retiring…'
                : 'Bringing it back…'
              : form.active
                ? 'Retire this form'
                : 'Use it again'}
          </Link>
          {lifeError && <span role="alert"> {lifeError}</span>}
        </p>
      )}
    </FormSheet>
  );
}
